import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAppStore } from '../stores/appStore';
import { api } from '../lib/api';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Button,
  Input,
} from '../components/ui';
import {
  Bot,
  Plus,
  Settings,
  Trash2,
  Power,
  PowerOff,
  GitBranch,
  MessageSquare,
  Search,
  Edit,
  Link as LinkIcon,
  Copy,
  Clock3,
  Sparkles,
} from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import type { RegisteredBot } from '../types';
import { isPartnerReadOnlyMode } from '../lib/readOnlyMode';

import { toast } from '../stores/toastStore';

const DUCK_WALK_LOTTIE_URL =
  'https://assets-v2.lottiefiles.com/a/66f2709c-1189-11ee-9713-6f537b50c88d/Pwo7meZamb.lottie';

export function Bots() {
  const {
    bots,
    flows,
    connections,
    createBot,
    updateBot,
    toggleBot,
    deleteBot,
    assignFlowToBot,
    unassignFlowFromBot,
    isLoading,
  } = useAppStore();

  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newBotName, setNewBotName] = useState('');
  const [newBotCode, setNewBotCode] = useState('');
  const [selectedConnectionId, setSelectedConnectionId] = useState('');
  const [selectedBotForFlow, setSelectedBotForFlow] = useState<string | null>(null);
  const [isAssigningFlow, setIsAssigningFlow] = useState(false);
  const [assigningFlowId, setAssigningFlowId] = useState<string | null>(null);
  const [isUnassigningFlow, setIsUnassigningFlow] = useState(false);
  const [unassigningBotId, setUnassigningBotId] = useState<string | null>(null);
  const [botToDelete, setBotToDelete] = useState<RegisteredBot | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [openLines, setOpenLines] = useState<Array<{ id: number; name: string; botId?: number }>>([]);
  const [selectedOpenLines, setSelectedOpenLines] = useState<number[]>([]);
  const [isLoadingOpenLines, setIsLoadingOpenLines] = useState(false);
  const [activeBotMenu, setActiveBotMenu] = useState<string | null>(null);
  const [botToRename, setBotToRename] = useState<RegisteredBot | null>(null);
  const [renameBotName, setRenameBotName] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  const [newlyCreatedBotId, setNewlyCreatedBotId] = useState<string | null>(null);
  const [botToConfigureReceptive, setBotToConfigureReceptive] = useState<RegisteredBot | null>(null);
  const [isSavingReceptive, setIsSavingReceptive] = useState(false);
  const [receptiveEnabled, setReceptiveEnabled] = useState(false);
  const [receptiveStartTime, setReceptiveStartTime] = useState('08:00');
  const [receptiveEndTime, setReceptiveEndTime] = useState('18:00');
  const [receptiveActiveDays, setReceptiveActiveDays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [receptiveBypassIfContacted24h, setReceptiveBypassIfContacted24h] = useState(false);
  const isReadOnlyMode = isPartnerReadOnlyMode();

  const normalizeOpenLines = (lines: any): Array<{ id: number; name: string; botId?: number }> => {
    const collection = Array.isArray(lines)
      ? lines
      : lines && typeof lines === 'object'
        ? Object.values(lines)
        : [];

    return collection
      .map((line: any) => ({
        // Use ID first (returned by imopenlines.config.list.get). Fallback to CONFIG_ID for compatibility.
        id: Number(line.ID || line.id || line.CONFIG_ID || line.configId),
        name:
          line.LINE_NAME ||
          line.lineName ||
          line.NAME ||
          line.name ||
          `Open Line ${line.CONFIG_ID || line.configId || line.ID || line.id}`,
        botId: line.BOT_ID || line.botId,
      }))
      .filter((line: any) => Number.isFinite(line.id) && line.id > 0);
  };

  // Load Open Lines when connection is selected
  useEffect(() => {
    if (selectedConnectionId) {
      setIsLoadingOpenLines(true);
      setOpenLines([]);
      setSelectedOpenLines([]);

      const loadOpenLines = async () => {
        try {
          const lines = await api.getOpenLines(selectedConnectionId);
          setOpenLines(normalizeOpenLines(lines));
          return;
        } catch (error) {
          console.warn('Open Lines load failed, attempting connection repair...', error);
        }

        try {
          await api.reconnectConnection(selectedConnectionId);
          const retriedLines = await api.getOpenLines(selectedConnectionId);
          setOpenLines(normalizeOpenLines(retriedLines));
          toast.success('Conexão corrigida e canais recarregados.');
        } catch (retryError: any) {
          console.error('Failed to load Open Lines after repair:', retryError);
          toast.error(retryError?.message || 'Erro ao carregar Open Lines');
          setOpenLines([]);
        } finally {
          setIsLoadingOpenLines(false);
        }
      };

      void loadOpenLines().finally(() => {
        setIsLoadingOpenLines(false);
      });
    } else {
      setOpenLines([]);
      setSelectedOpenLines([]);
      setIsLoadingOpenLines(false);
    }
  }, [selectedConnectionId]);

  const toggleOpenLine = (lineId: number) => {
    setSelectedOpenLines((prev) =>
      prev.includes(lineId)
        ? prev.filter((id) => id !== lineId)
        : [...prev, lineId]
    );
  };

  // Data is fetched in App.tsx


  const filteredBots = bots.filter(
    (bot) =>
      bot.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bot.code.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const selectedBot = selectedBotForFlow
    ? bots.find((b) => b.id === selectedBotForFlow) || null
    : null;
  const selectedBotFlow = selectedBot?.flowId
    ? flows.find((f) => f.id === selectedBot.flowId) || null
    : null;

  const handleCreateBot = async () => {
    if (isReadOnlyMode) return;
    if (!newBotName.trim() || !newBotCode.trim() || !selectedConnectionId) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    if (isCreating) return; // Prevent double-click

    setIsCreating(true);
    try {
      const animationStartAt = Date.now();
      const minimumAnimationMs = 1500;

      const createdBot = await createBot(selectedConnectionId, {
        name: newBotName,
        code: newBotCode,
        openLineIds: selectedOpenLines.length > 0 ? selectedOpenLines : undefined,
      });

      const elapsedMs = Date.now() - animationStartAt;
      if (elapsedMs < minimumAnimationMs) {
        await new Promise((resolve) => setTimeout(resolve, minimumAnimationMs - elapsedMs));
      }

      toast.success('Bot criado com sucesso!');
      setShowCreateModal(false);
      setNewBotName('');
      setNewBotCode('');
      setSelectedConnectionId('');
      setSelectedOpenLines([]);

      setNewlyCreatedBotId(createdBot.id);
      setTimeout(() => {
        const card = document.getElementById(`bot-card-${createdBot.id}`);
        card?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 120);
      setTimeout(() => setNewlyCreatedBotId(null), 2600);
    } catch (error: any) {
      console.error('Failed to create bot:', error);
      
      // Handle duplicate bot code error
      if (error.response?.status === 409 || error.message?.includes('already exists')) {
        const errorData = error.response?.data;
        if (errorData?.existingBot) {
          toast.error(`O código "@${errorData.existingBot.code}" já está em uso pelo bot "${errorData.existingBot.name}". Escolha outro código.`);
        } else {
          toast.error('Este código de bot já está em uso. Escolha outro código.');
        }
      } else {
        toast.error(error.response?.data?.message || error.message || 'Erro ao criar bot. Tente novamente.');
      }
    } finally {
      setIsCreating(false);
    }
  };

  const getStatusBadge = (status: RegisteredBot['status']) => {
    const config = {
      active: { variant: 'success' as const, label: 'Ativo' },
      inactive: { variant: 'default' as const, label: 'Inativo' },
      error: { variant: 'danger' as const, label: 'Erro' },
    };
    const { variant, label } = config[status];
    return <Badge variant={variant}>{label}</Badge>;
  };

  const handleToggleBot = async (bot: RegisteredBot) => {
    if (isReadOnlyMode) return;
    await toggleBot(bot.id, bot.status !== 'active');
  };

  const handleAssignFlow = async (botId: string, flowId: string) => {
    if (isReadOnlyMode) return;
    if (isAssigningFlow || isUnassigningFlow) return;

    setIsAssigningFlow(true);
    setAssigningFlowId(flowId);
    try {
      await assignFlowToBot(botId, flowId);
      toast.success('Fluxo associado com sucesso!');
      setSelectedBotForFlow(null);
    } catch (error: any) {
      console.error('Failed to assign flow to bot:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Erro ao associar fluxo.');
    } finally {
      setIsAssigningFlow(false);
      setAssigningFlowId(null);
    }
  };

  const handleUnassignFlow = async (botId: string, closeModal = false) => {
    if (isReadOnlyMode) return;
    if (isAssigningFlow || isUnassigningFlow) return;

    setIsUnassigningFlow(true);
    setUnassigningBotId(botId);
    try {
      await unassignFlowFromBot(botId);
      toast.success('Fluxo desvinculado com sucesso!');
      if (closeModal) {
        setSelectedBotForFlow(null);
      }
    } catch (error: any) {
      console.error('Failed to unassign flow from bot:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Erro ao desvincular fluxo.');
    } finally {
      setIsUnassigningFlow(false);
      setUnassigningBotId(null);
    }
  };

  const openRenameModal = (bot: RegisteredBot) => {
    if (isReadOnlyMode) return;
    setBotToRename(bot);
    setRenameBotName(bot.name);
    setActiveBotMenu(null);
  };

  const handleRenameBot = async () => {
    if (!botToRename || !renameBotName.trim() || isRenaming) return;

    setIsRenaming(true);
    try {
      await updateBot(botToRename.id, { name: renameBotName.trim() });
      toast.success('Nome do bot atualizado com sucesso!');
      setBotToRename(null);
      setRenameBotName('');
    } catch (error) {
      console.error(error);
      toast.error('Erro ao renomear bot. Tente novamente.');
    } finally {
      setIsRenaming(false);
    }
  };

  const handleCopyBotCode = async (bot: RegisteredBot) => {
    const codeToCopy = `@${bot.code}`;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(codeToCopy);
        toast.success(`Código ${codeToCopy} copiado!`);
        return;
      }

      throw new Error('Clipboard API indisponível');
    } catch (error) {
      console.warn('Clipboard API bloqueada, tentando fallback com execCommand.', error);

      try {
        const textArea = document.createElement('textarea');
        textArea.value = codeToCopy;
        textArea.setAttribute('readonly', '');
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        textArea.style.pointerEvents = 'none';

        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        const copied = document.execCommand('copy');
        document.body.removeChild(textArea);

        if (copied) {
          toast.success(`Código ${codeToCopy} copiado!`);
          return;
        }

        throw new Error('execCommand retornou false');
      } catch (fallbackError) {
        console.warn('Fallback execCommand falhou, exibindo prompt manual.', fallbackError);
        window.prompt('Copie manualmente o código do bot:', codeToCopy);
        toast.error('Cópia automática bloqueada neste ambiente.');
      }
    } finally {
      setActiveBotMenu(null);
    }
  };

  const openReceptiveConfigModal = (bot: RegisteredBot) => {
    if (isReadOnlyMode) return;
    const receptiveSettings = bot.settings?.receptiveOnly;

    setBotToConfigureReceptive(bot);
    setReceptiveEnabled(receptiveSettings?.enabled === true);
    setReceptiveStartTime(receptiveSettings?.startTime || '08:00');
    setReceptiveEndTime(receptiveSettings?.endTime || '18:00');
    setReceptiveActiveDays(
      Array.isArray(receptiveSettings?.activeDays) && receptiveSettings.activeDays.length > 0
        ? receptiveSettings.activeDays
        : [1, 2, 3, 4, 5],
    );
    setReceptiveBypassIfContacted24h(receptiveSettings?.bypassIfContactedLast24h === true);
    setActiveBotMenu(null);
  };

  const toggleReceptiveDay = (day: number) => {
    setReceptiveActiveDays((prev) =>
      prev.includes(day) ? prev.filter((value) => value !== day) : [...prev, day].sort((a, b) => a - b),
    );
  };

  const handleSaveReceptiveConfig = async () => {
    if (isReadOnlyMode) return;
    if (!botToConfigureReceptive || isSavingReceptive) return;

    if (receptiveEnabled && receptiveActiveDays.length === 0) {
      toast.error('Selecione pelo menos um dia ativo para o modo receptivo.');
      return;
    }

    setIsSavingReceptive(true);
    try {
      await updateBot(botToConfigureReceptive.id, {
        settings: {
          receptiveOnly: {
            enabled: receptiveEnabled,
            activeDays: receptiveActiveDays,
            startTime: receptiveStartTime,
            endTime: receptiveEndTime,
            bypassIfContactedLast24h: receptiveBypassIfContacted24h,
          },
        },
      });

      toast.success('Configuração de receptivo salva com sucesso!');
      setBotToConfigureReceptive(null);
    } catch (error) {
      console.error(error);
      toast.error('Não foi possível salvar a configuração de receptivo.');
    } finally {
      setIsSavingReceptive(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="h-8 w-24 bg-slate-200 dark:bg-slate-700 rounded" />
            <div className="h-4 w-56 bg-slate-200 dark:bg-slate-700 rounded" />
          </div>
          <div className="h-10 w-28 bg-slate-200 dark:bg-slate-700 rounded-lg" />
        </div>

        <div className="h-10 w-full max-w-md bg-slate-200 dark:bg-slate-700 rounded-lg" />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 space-y-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 bg-slate-200 dark:bg-slate-700 rounded-lg" />
                  <div className="space-y-2">
                    <div className="h-5 w-28 bg-slate-200 dark:bg-slate-700 rounded" />
                    <div className="h-4 w-20 bg-slate-200 dark:bg-slate-700 rounded" />
                  </div>
                </div>
                <div className="h-6 w-16 bg-slate-200 dark:bg-slate-700 rounded-full" />
              </div>
              <div className="space-y-2">
                <div className="h-4 w-44 bg-slate-200 dark:bg-slate-700 rounded" />
                <div className="h-4 w-36 bg-slate-200 dark:bg-slate-700 rounded" />
              </div>
              <div className="pt-4 border-t border-slate-100 dark:border-slate-700 flex items-center gap-2">
                <div className="h-8 flex-1 bg-slate-200 dark:bg-slate-700 rounded" />
                <div className="h-8 w-8 bg-slate-200 dark:bg-slate-700 rounded" />
                <div className="h-8 w-8 bg-slate-200 dark:bg-slate-700 rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {isReadOnlyMode && (
        <Card className="bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/30">
          <CardContent className="py-3 text-sm text-amber-900 dark:text-amber-200">
            Modo leitura ativo: criação e edição de bots estão desabilitadas neste tenant.
          </CardContent>
        </Card>
      )}

      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Bots</h1>
          <p className="text-slate-500 dark:text-slate-300 mt-1">
            Gerencie seus bots de atendimento
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} disabled={isReadOnlyMode}>
          <Plus className="w-4 h-4 mr-2" />
          Novo Bot
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <Input
          placeholder="Buscar bots..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Bots Grid */}
      {filteredBots.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Bot className="w-16 h-16 text-slate-300 dark:text-slate-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
                {searchTerm ? 'Nenhum bot encontrado' : 'Nenhum bot criado'}
              </h3>
              <p className="text-slate-500 dark:text-slate-300 mb-4">
                {searchTerm
                  ? 'Tente buscar com outros termos'
                  : 'Crie seu primeiro bot para começar a automatizar atendimentos'}
              </p>
              {!searchTerm && (
                <Button onClick={() => setShowCreateModal(true)} disabled={isReadOnlyMode}>
                  <Plus className="w-4 h-4 mr-2" />
                  Criar Bot
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredBots.map((bot) => {
            const connection = connections.find((c) => c.id === bot.connectionId);
            const flow = flows.find((f) => f.id === bot.flowId);

            return (
              <motion.div
                key={bot.id}
                layout
                initial={{ opacity: 0, y: 12, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.24, ease: 'easeOut' }}
              >
                <Card
                  id={`bot-card-${bot.id}`}
                  className={`hover:shadow-md transition-all duration-500 ${
                    newlyCreatedBotId === bot.id
                      ? 'ring-2 ring-blue-400 shadow-lg animate-pulse'
                      : ''
                  }`}
                >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-12 h-12 rounded-lg flex items-center justify-center"
                        style={{
                          backgroundColor: bot.color
                            ? `${bot.color}20`
                            : '#dbeafe',
                        }}
                      >
                        <Bot
                          className="w-6 h-6"
                          style={{ color: bot.color || '#2563eb' }}
                        />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900 dark:text-slate-100">{bot.name}</h3>
                        <p className="text-sm text-slate-500 dark:text-slate-300">@{bot.code}</p>
                      </div>
                    </div>
                    {getStatusBadge(bot.status)}
                  </div>

                  {bot.description && (
                    <p className="text-sm text-slate-600 dark:text-slate-300 mb-4 line-clamp-2">
                      {bot.description}
                    </p>
                  )}

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-300">
                      <MessageSquare className="w-4 h-4" />
                      <span>
                        {connection?.portalDomain || 'Portal não encontrado'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-300">
                      <GitBranch className="w-4 h-4" />
                      {flow ? (
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/flows/${flow.id}`}
                            className="text-blue-600 hover:underline"
                          >
                            {flow.name}
                          </Link>
                          <button
                            onClick={() => {
                              if (window.confirm('Deseja desvincular este fluxo do bot?')) {
                                void handleUnassignFlow(bot.id);
                              }
                            }}
                            disabled={isReadOnlyMode || isAssigningFlow || isUnassigningFlow}
                            className="text-xs text-slate-500 hover:text-red-500 disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            {isUnassigningFlow && unassigningBotId === bot.id
                              ? 'Desvinculando...'
                              : 'Desvincular'}
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setSelectedBotForFlow(bot.id)}
                          disabled={isReadOnlyMode}
                          className="text-blue-600 hover:underline"
                        >
                          Associar fluxo
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 pt-4 border-t border-slate-100 dark:border-slate-700">
                    <Button
                      variant={bot.status === 'active' ? 'outline' : 'primary'}
                      size="sm"
                      onClick={() => handleToggleBot(bot)}
                      disabled={isReadOnlyMode}
                      className="flex-1"
                    >
                      {bot.status === 'active' ? (
                        <>
                          <PowerOff className="w-4 h-4 mr-1" />
                          Desativar
                        </>
                      ) : (
                        <>
                          <Power className="w-4 h-4 mr-1" />
                          Ativar
                        </>
                      )}
                    </Button>
                    <div className="relative">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setActiveBotMenu(activeBotMenu === bot.id ? null : bot.id)
                        }
                        disabled={isReadOnlyMode}
                      >
                        <Settings className="w-4 h-4" />
                      </Button>
                      <AnimatePresence>
                        {activeBotMenu === bot.id && (
                        <motion.div
                          initial={{ opacity: 0, y: -6, scale: 0.98 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -4, scale: 0.98 }}
                          transition={{ duration: 0.16, ease: 'easeOut' }}
                          className="absolute right-0 top-10 w-56 bg-white dark:bg-slate-900 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 py-1 z-20"
                        >
                          <button
                            onClick={() => openRenameModal(bot)}
                            disabled={isReadOnlyMode}
                            className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 w-full"
                          >
                            <Edit className="w-4 h-4" />
                            Renomear bot
                          </button>
                          <button
                            onClick={() => {
                              setSelectedBotForFlow(bot.id);
                              setActiveBotMenu(null);
                            }}
                            disabled={isReadOnlyMode || isUnassigningFlow}
                            className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 w-full"
                          >
                            <LinkIcon className="w-4 h-4" />
                            {bot.flowId ? 'Trocar fluxo' : 'Associar fluxo'}
                          </button>
                          {bot.flowId && (
                            <button
                              onClick={() => {
                                setActiveBotMenu(null);
                                if (window.confirm('Deseja desvincular este fluxo do bot?')) {
                                  void handleUnassignFlow(bot.id);
                                }
                              }}
                              disabled={isReadOnlyMode || isAssigningFlow || isUnassigningFlow}
                              className="flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 w-full disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                              <LinkIcon className="w-4 h-4" />
                              {isUnassigningFlow && unassigningBotId === bot.id
                                ? 'Desvinculando...'
                                : 'Desvincular fluxo'}
                            </button>
                          )}
                          <button
                            onClick={() => handleCopyBotCode(bot)}
                            className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 w-full"
                          >
                            <Copy className="w-4 h-4" />
                            Copiar @código do bot
                          </button>
                          <button
                            onClick={() => openReceptiveConfigModal(bot)}
                            disabled={isReadOnlyMode}
                            className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 w-full"
                          >
                            <Clock3 className="w-4 h-4" />
                            Configurar apenas receptivo
                          </button>
                        </motion.div>
                      )}
                      </AnimatePresence>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setBotToDelete(bot)}
                      disabled={isReadOnlyMode}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Flow Selection Modal */}
      <AnimatePresence>
      {selectedBotForFlow && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
        >
          <motion.div
            initial={{ opacity: 0, y: 14, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="w-full max-w-md mx-4"
          >
          <Card>
            <CardHeader>
              <CardTitle>Selecionar Fluxo</CardTitle>
              {selectedBotFlow && (
                <p className="text-sm text-slate-500 dark:text-slate-300">
                  Fluxo atual: <span className="font-medium text-slate-700 dark:text-slate-200">{selectedBotFlow.name}</span>
                </p>
              )}
            </CardHeader>
            <CardContent>
              {flows.filter((f) => f.status === 'published').length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-slate-500 dark:text-slate-300 mb-4">
                    Nenhum fluxo publicado disponível
                  </p>
                  <Link to="/flows/new">
                    <Button disabled={isReadOnlyMode}>Criar Fluxo</Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-2">
                  {flows
                    .filter((f) => f.status === 'published')
                    .map((flow) => (
                      <button
                        key={flow.id}
                        onClick={() => handleAssignFlow(selectedBotForFlow, flow.id)}
                        disabled={isReadOnlyMode || isAssigningFlow || isUnassigningFlow}
                        className={`w-full p-3 text-left rounded-lg border border-slate-200 dark:border-slate-700 transition-colors ${
                          isAssigningFlow || isUnassigningFlow
                            ? 'cursor-not-allowed opacity-70'
                            : 'hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-500/10'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-medium text-slate-900 dark:text-slate-100">{flow.name}</p>
                            {flow.description && (
                              <p className="text-sm text-slate-500 dark:text-slate-300 mt-1">
                                {flow.description}
                              </p>
                            )}
                          </div>
                          {isAssigningFlow && assigningFlowId === flow.id && (
                            <div className="flex items-center gap-2 text-xs text-blue-600 dark:text-blue-300 whitespace-nowrap">
                              <svg
                                className="animate-spin h-4 w-4"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                />
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                />
                              </svg>
                              Associando...
                            </div>
                          )}
                        </div>
                      </button>
                    ))}
                </div>
              )}
              {selectedBot?.flowId && (
                <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-700">
                  <Button
                    variant="outline"
                    className="w-full text-red-600 border-red-300 hover:bg-red-50 dark:text-red-300 dark:border-red-500/50 dark:hover:bg-red-500/10"
                    onClick={() => handleUnassignFlow(selectedBot.id, true)}
                    disabled={isAssigningFlow || isUnassigningFlow}
                  >
                    {isUnassigningFlow && unassigningBotId === selectedBot.id
                      ? 'Desvinculando...'
                      : 'Desvincular fluxo atual'}
                  </Button>
                </div>
              )}
              <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-700">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setSelectedBotForFlow(null)}
                  disabled={isAssigningFlow || isUnassigningFlow}
                >
                  Cancelar
                </Button>
              </div>
            </CardContent>
          </Card>
          </motion.div>
        </motion.div>
      )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
      {botToDelete && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
        >
          <motion.div
            initial={{ opacity: 0, y: 14, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="w-full max-w-md mx-4"
          >
          <Card>
            <CardHeader>
              <CardTitle className="text-red-600">Excluir Bot</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600 dark:text-slate-300 mb-6">
                Tem certeza que deseja excluir o bot <strong>{botToDelete.name}</strong>? 
                Esta ação não pode ser desfeita.
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setBotToDelete(null)}
                  disabled={isDeleting}
                >
                  Cancelar
                </Button>
                <Button
                  variant="danger"
                  className="flex-1"
                  disabled={isDeleting}
                  onClick={async () => {
                    if (botToDelete) {
                      setIsDeleting(true);
                      try {
                        const loadingToast = toast.loading('Excluindo bot...');
                        await deleteBot(botToDelete.id);
                        toast.dismiss(loadingToast);
                        toast.success('Bot excluído com sucesso');
                      } catch (error) {
                        console.error(error);
                        toast.error('Erro ao excluir bot. Tente novamente.');
                      } finally {
                        setIsDeleting(false);
                        setBotToDelete(null);
                      }
                    }
                  }}
                >
                  {isDeleting ? 'Excluindo...' : 'Excluir'}
                </Button>
              </div>
            </CardContent>
          </Card>
          </motion.div>
        </motion.div>
      )}
      </AnimatePresence>

      {/* Rename Bot Modal */}
      <AnimatePresence>
      {botToRename && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
        >
          <motion.div
            initial={{ opacity: 0, y: 14, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="w-full max-w-md mx-4"
          >
          <Card>
            <CardHeader>
              <CardTitle>Renomear Bot</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <Input
                  label="Nome do Bot"
                  placeholder="Ex: Atendimento Vendas"
                  value={renameBotName}
                  onChange={(e) => setRenameBotName(e.target.value)}
                />
                <p className="text-xs text-slate-500 dark:text-slate-300">
                  Código atual: <strong>@{botToRename.code}</strong>
                </p>
              </div>
              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setBotToRename(null);
                    setRenameBotName('');
                  }}
                  disabled={isRenaming}
                >
                  Cancelar
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleRenameBot}
                  disabled={!renameBotName.trim() || isRenaming}
                >
                  {isRenaming ? 'Salvando...' : 'Salvar Nome'}
                </Button>
              </div>
            </CardContent>
          </Card>
          </motion.div>
        </motion.div>
      )}
      </AnimatePresence>

      {/* Create Bot Modal */}
      <AnimatePresence>
      {showCreateModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
        >
          <motion.div
            initial={{ opacity: 0, y: 14, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="w-full max-w-md mx-4"
          >
          <Card>
            <CardHeader>
              <CardTitle>Criar Novo Bot</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {isCreating && (
                  <motion.div
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    className="px-1 py-1"
                  >
                    <div className="text-sm text-blue-700 dark:text-blue-300 mb-2">Carregando ambiente de criação do bot...</div>
                    <div className="relative h-12">
                      <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-1.5 rounded-full bg-blue-100" />
                      <motion.div
                        className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-1.5 rounded-full bg-blue-500 origin-left"
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
                      />
                      <motion.div
                        className="absolute top-1/2 -translate-y-1/2 h-10 w-10"
                        initial={{ left: '0%' }}
                        animate={{ left: 'calc(100% - 2.5rem)' }}
                        transition={{ duration: 1.5, repeat: Infinity, repeatType: 'loop', ease: 'linear' }}
                      >
                        <DotLottieReact
                          src={DUCK_WALK_LOTTIE_URL}
                          loop
                          autoplay
                          className="h-full w-full"
                        />
                      </motion.div>
                    </div>
                  </motion.div>
                )}
                <Input
                  label="Nome do Bot"
                  placeholder="Ex: Atendimento Vendas"
                  value={newBotName}
                  onChange={(e) => setNewBotName(e.target.value)}
                />
                <div>
                  <Input
                    label="Código (Identificador)"
                    placeholder="Ex: vendas_bot"
                    value={newBotCode}
                    onChange={(e) => setNewBotCode(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-300 mt-1">
                    Use apenas letras minúsculas, números e underscore. Deve ser único.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                    Conexão Bitrix24
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={selectedConnectionId}
                    onChange={(e) => setSelectedConnectionId(e.target.value)}
                  >
                    <option value="">Selecione uma conexão...</option>
                    {connections.map((conn) => (
                      <option key={conn.id} value={conn.id}>
                        {conn.portalDomain}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Open Lines Selection */}
                {selectedConnectionId && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">
                      Canais de Atendimento (Open Lines)
                    </label>
                    {isLoadingOpenLines ? (
                      <div className="flex items-center gap-2 text-slate-500 dark:text-slate-300 text-sm py-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        Carregando canais...
                      </div>
                    ) : openLines.length === 0 ? (
                      <p className="text-sm text-slate-500 dark:text-slate-300">Nenhum canal encontrado</p>
                    ) : (
                      <div className="max-h-40 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-lg">
                        {openLines.map((line) => (
                          <label
                            key={line.id}
                            className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer border-b border-slate-100 dark:border-slate-700 last:border-0"
                          >
                            <input
                              type="checkbox"
                              checked={selectedOpenLines.includes(line.id)}
                              onChange={() => toggleOpenLine(line.id)}
                              className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-slate-700 dark:text-slate-200 flex-1">{line.name}</span>
                            {line.botId && (
                              <span className="text-xs text-slate-400 dark:text-slate-400">Bot #{line.botId}</span>
                            )}
                          </label>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-slate-500 dark:text-slate-300 mt-1">
                      {selectedOpenLines.length === 0 
                        ? 'Nenhum canal selecionado. O bot será criado sem associação a canais.'
                        : `${selectedOpenLines.length} canal(is) selecionado(s)`}
                    </p>
                  </div>
                )}
              </div>
              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewBotName('');
                    setNewBotCode('');
                    setSelectedConnectionId('');
                    setSelectedOpenLines([]);
                  }}
                  disabled={isCreating}
                >
                  Cancelar
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleCreateBot}
                  disabled={isReadOnlyMode || !newBotName.trim() || !newBotCode.trim() || !selectedConnectionId || isCreating}
                >
                  <AnimatePresence mode="wait" initial={false}>
                    {isCreating ? (
                      <motion.span
                        key="creating"
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.15 }}
                        className="inline-flex items-center gap-2"
                      >
                        <motion.span
                          animate={{ rotate: 360 }}
                          transition={{ duration: 0.9, repeat: Infinity, ease: 'linear' }}
                        >
                          <Sparkles className="h-4 w-4" />
                        </motion.span>
                        Criando...
                      </motion.span>
                    ) : (
                      <motion.span
                        key="create"
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.15 }}
                      >
                        Criar Bot
                      </motion.span>
                    )}
                  </AnimatePresence>
                </Button>
              </div>
            </CardContent>
          </Card>
          </motion.div>
        </motion.div>
      )}
      </AnimatePresence>

      {/* Receptive Mode Modal */}
      {botToConfigureReceptive && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg mx-4">
            <CardHeader>
              <CardTitle>Modo Apenas Receptivo</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between py-2 px-3 border border-slate-200 dark:border-slate-700 rounded-md">
                <div>
                  <p className="font-medium text-slate-900 dark:text-slate-100">Ativar modo receptivo por horário</p>
                  <p className="text-sm text-slate-500 dark:text-slate-300">Quando o cliente responde dentro do horário e dos dias configurados abaixo, é direcionado diretamente para a fila humana sem passar pelo fluxo do bot.</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer ml-4 shrink-0">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={receptiveEnabled}
                    onChange={(e) => {
                      setReceptiveEnabled(e.target.checked);
                      if (e.target.checked) setReceptiveBypassIfContacted24h(false);
                    }}
                  />
                  <div className="w-11 h-6 bg-slate-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {receptiveEnabled && (
                <div>
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">Dias ativos</p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { id: 0, label: 'Dom' },
                      { id: 1, label: 'Seg' },
                      { id: 2, label: 'Ter' },
                      { id: 3, label: 'Qua' },
                      { id: 4, label: 'Qui' },
                      { id: 5, label: 'Sex' },
                      { id: 6, label: 'Sáb' },
                    ].map((day) => (
                      <button
                        key={day.id}
                        type="button"
                        onClick={() => toggleReceptiveDay(day.id)}
                        disabled={isReadOnlyMode}
                        className={`px-3 py-1.5 text-xs rounded-md border ${
                          receptiveActiveDays.includes(day.id)
                            ? 'bg-blue-50 border-blue-300 text-blue-700'
                            : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300'
                        }`}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between py-2 px-3 border border-slate-200 dark:border-slate-700 rounded-md">
                <div>
                  <p className="font-medium text-slate-900 dark:text-slate-100">Direcionar se contatado nas últimas 24h</p>
                  <p className="text-sm text-slate-500 dark:text-slate-300">Se enviamos uma mensagem ativa ao cliente nas últimas 24h (contadas a partir da resposta dele), ele é direcionado direto para a fila humana sem passar pelo fluxo do bot.</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer ml-4 shrink-0">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={receptiveBypassIfContacted24h}
                    onChange={(e) => {
                      setReceptiveBypassIfContacted24h(e.target.checked);
                      if (e.target.checked) setReceptiveEnabled(false);
                    }}
                    disabled={isReadOnlyMode}
                  />
                  <div className="w-11 h-6 bg-slate-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setBotToConfigureReceptive(null)}
                  disabled={isSavingReceptive}
                >
                  Cancelar
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleSaveReceptiveConfig}
                  disabled={isReadOnlyMode || isSavingReceptive}
                >
                  {isSavingReceptive ? 'Salvando...' : 'Salvar Configuração'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Click outside to close bot settings menu */}
      {activeBotMenu && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => setActiveBotMenu(null)}
        />
      )}
    </div>
  );
}
